/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from "react";
import { 
  Box, 
  Checkbox,
} from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import {
  isBitSet,
  getAlarmLedColor,
} from "../utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";

/**
 * FCM/FMM/FRM Device Details Component
 * Displays optical loss information for 3 fiber links with charts and alarms for FCM/FMM/FRM devices
 */
const FcmDeviceDetails: React.FC<DeviceDetailsProps> = ({ device, parsedAttributes, statusLabel, alarmsOnly = false }) => {
  const isDisconnected = statusLabel.toLowerCase() === "disconnected";

  // Extract optical link data for links 1-3
  const opticalLinks = [];
  for (let i = 1; i <= 3; i++) {
    const dlKey = `dlOpticalLossLink${i}`;
    const ulKey = `ulOpticalLossLink${i}`;
    const dl = parsedAttributes[dlKey];
    const ul = parsedAttributes[ulKey];
    
    if (dl !== undefined || ul !== undefined) {
      opticalLinks.push({ 
        link: i, 
        name: `Link ${i}`,
        dl, 
        ul 
      });
    }
  }

  const hasOpticalData = opticalLinks.length > 0;

  // Extract alarm values
  const fcmAlarms = parsedAttributes.fcmAlarms || 0;
  const fcmAlarmsMask = parsedAttributes.fcmAlarmsMask || 0;

  // Global (non-port) alarms
  const globalAlarmDefinitions: AlarmDefinition[] = useMemo(
    () => [
      { bit: 4, name: "Over Temperature" },
      { bit: 5, name: "Inconsistent Version" },
      { bit: 6, name: "Adjustment Fault" },
      { bit: 7, name: "HW Failure" },
      { bit: 8, name: "MNG Optical Power Low" },
      { bit: 9, name: "Clock Reference Cutting" },
      { bit: 10, name: "FMM Optical Loss" },
    ],
    []
  );

  // Port-specific optical alarms
  const portAlarmDefinitions = useMemo(
    () => [
      { name: "Optical Power Low", bits: [1, 2, 3] },  // bits for Port 1, 2, 3
    ],
    []
  );

  const hasAlarms = fcmAlarms !== undefined && fcmAlarms !== null;

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasAlarms) return null;
    
    return (
      <>
        {hasAlarms && (
          <>
            <Card className="mb-2 overflow-hidden">
              <CardHeader className="pb-3 px-4 pt-4">
                <CardTitle className="text-sm">Alarms</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <AlarmTable
                  alarmDefinitions={globalAlarmDefinitions}
                  alarms={fcmAlarms}
                  alarmsMask={fcmAlarmsMask}
                  isDisconnected={isDisconnected}
                />
              </CardContent>
            </Card>

            <Card className="mb-2 overflow-hidden">
              <CardHeader className="pb-3 px-4 pt-4">
                <CardTitle className="text-sm">Optical Port Alarms</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold text-muted-foreground min-w-[180px]">Alarm</TableHead>
                      <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 1</TableHead>
                      <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 2</TableHead>
                      <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 3</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portAlarmDefinitions.map((alarmDef, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{alarmDef.name}</TableCell>
                        {[0, 1, 2].map((portIdx) => {
                          const bit = alarmDef.bits[portIdx];
                          const isAlarmActive = isBitSet(fcmAlarms, bit);
                          const isMaskEnabled = isBitSet(fcmAlarmsMask, bit);
                          return (
                            <TableCell key={portIdx} className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive) }}
                                />
                                <Checkbox
                                  checked={isMaskEnabled}
                                  disabled
                                  size="small"
                                  sx={{
                                    padding: 0,
                                    color: "var(--foreground)",
                                    "&.Mui-disabled": {
                                      color: "var(--muted-foreground)",
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </>
    );
  }

  // Normal mode: show optical data and alarms
  // Normal mode: show alarms only (charts removed)
  if (!hasAlarms) return null;

  return (
    <>
      {hasAlarms && (
        <>
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarmDefinitions={globalAlarmDefinitions}
                alarms={fcmAlarms}
                alarmsMask={fcmAlarmsMask}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>

          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Optical Port Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold text-muted-foreground min-w-[180px]">Alarm</TableHead>
                    <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 1</TableHead>
                    <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 2</TableHead>
                    <TableHead className="font-semibold text-muted-foreground text-center min-w-[120px]">Port 3</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portAlarmDefinitions.map((alarmDef, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{alarmDef.name}</TableCell>
                      {[0, 1, 2].map((portIdx) => {
                        const bit = alarmDef.bits[portIdx];
                        const isAlarmActive = isBitSet(fcmAlarms, bit);
                        const isMaskEnabled = isBitSet(fcmAlarmsMask, bit);
                        return (
                          <TableCell key={portIdx} className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive) }}
                              />
                              <Checkbox
                                checked={isMaskEnabled}
                                disabled
                                size="small"
                                sx={{
                                  padding: 0,
                                  color: "var(--foreground)",
                                  "&.Mui-disabled": {
                                    color: "var(--muted-foreground)",
                                  },
                                }}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default FcmDeviceDetails;
